import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";

// Layout constants — all positions defined here for easy alignment
const QUERY_X = 120;
const QUERY_Y = 380;
const QUERY_W = 400;

const LLM_X = 760; // center X of brain
const LLM_Y = 430; // center Y of brain
const LLM_R = 70;  // radius

const EXPANDED_X = 1020;
const EXPANDED_Y = 230;
const EXPANDED_W = 480;

export const Scene3QueryRewrite: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  const exitOpacity = interpolate(frame, [270, 300], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const originalAppear = spring({ frame: frame - 15, fps, config: SPRING.gentle });
  const llmAppear = spring({ frame: frame - 60, fps, config: SPRING.bouncy });

  const expandedQueries = [
    "EAKC platform architecture multi-tenant knowledge management",
    "RAG implementation code flow retrieval augmented generation",
    "context updates April 4 2026 March 31 2026 sessions",
    "hybrid search vector BM25 reranking pipeline",
    "document ingestion sync worker chunking embedding",
  ];

  const isProcessing = frame > 60 && frame < 110;

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.06} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 60,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
        }}
      >
        <div style={{ fontSize: 42, fontFamily: FONTS.heading, fontWeight: 700, color: COLORS.textPrimary }}>
          Step 2:{" "}
          <span style={{ color: COLORS.accentPurple }}>Rewrite, Decompose & Cache</span>
        </div>
        <div style={{ fontSize: 18, fontFamily: FONTS.body, color: COLORS.textSecondary, marginTop: 8 }}>
          Rewrite → source-aware decomposition into sub-queries → semantic cache check
        </div>
      </div>

      {/* === Original query box — left === */}
      <div
        style={{
          position: "absolute",
          left: QUERY_X,
          top: QUERY_Y - 30,
          width: QUERY_W,
          opacity: interpolate(originalAppear, [0, 1], [0, 1]),
          transform: `translateX(${interpolate(originalAppear, [0, 1], [-30, 0])}px)`,
        }}
      >
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 12,
            color: COLORS.textMuted,
            marginBottom: 10,
            textTransform: "uppercase",
            letterSpacing: 1.5,
          }}
        >
          Original Query
        </div>
        <div
          style={{
            padding: "20px 24px",
            borderRadius: 14,
            background: COLORS.bgCard,
            border: `1.5px solid ${COLORS.accentBlue}40`,
            fontFamily: FONTS.body,
            fontSize: 16,
            color: COLORS.textPrimary,
            lineHeight: 1.6,
          }}
        >
          &ldquo;What is the EAKC platform and what are the latest context for April 4 and March 31, 2026? Explain the code flow of RAG.&rdquo;
        </div>
      </div>

      {/* === Arrow: Query → LLM === */}
      <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "visible" }}>
        {/* Arrow from query box right edge to LLM circle left edge */}
        {(() => {
          const arrowAppear = spring({ frame: frame - 40, fps, config: SPRING.gentle });
          const progress = interpolate(arrowAppear, [0, 1], [0, 1]);

          const startX = QUERY_X + QUERY_W + 10;
          const startY = LLM_Y;
          const endX = LLM_X - LLM_R - 5;
          const endY = LLM_Y;

          const curX = startX + (endX - startX) * progress;

          return (
            <line
              x1={startX}
              y1={startY}
              x2={curX}
              y2={endY}
              stroke={COLORS.accentPurple}
              strokeWidth={2}
              opacity={interpolate(arrowAppear, [0, 0.1], [0, 0.8])}
              markerEnd={progress > 0.9 ? "url(#arrowHead1)" : undefined}
            />
          );
        })()}

        {/* Arrow from LLM circle right edge to expanded queries */}
        {(() => {
          const arrowAppear = spring({ frame: frame - 85, fps, config: SPRING.gentle });
          const progress = interpolate(arrowAppear, [0, 1], [0, 1]);

          const startX = LLM_X + LLM_R + 5;
          const startY = LLM_Y;
          const endX = EXPANDED_X - 10;
          const endY = EXPANDED_Y + 120; // aim at middle of the list

          const curX = startX + (endX - startX) * progress;
          const curY = startY + (endY - startY) * progress;

          return (
            <line
              x1={startX}
              y1={startY}
              x2={curX}
              y2={curY}
              stroke={COLORS.accentPurple}
              strokeWidth={2}
              opacity={interpolate(arrowAppear, [0, 0.1], [0, 0.8])}
              markerEnd={progress > 0.9 ? "url(#arrowHead2)" : undefined}
            />
          );
        })()}

        {/* Arrow markers */}
        <defs>
          <marker id="arrowHead1" markerWidth="10" markerHeight="8" refX="9" refY="4" orient="auto">
            <polygon points="0 0, 10 4, 0 8" fill={COLORS.accentPurple} opacity={0.8} />
          </marker>
          <marker id="arrowHead2" markerWidth="10" markerHeight="8" refX="9" refY="4" orient="auto">
            <polygon points="0 0, 10 4, 0 8" fill={COLORS.accentPurple} opacity={0.8} />
          </marker>
        </defs>
      </svg>

      {/* === LLM Brain — center === */}
      <div
        style={{
          position: "absolute",
          left: LLM_X - LLM_R,
          top: LLM_Y - LLM_R,
          width: LLM_R * 2,
          height: LLM_R * 2,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${COLORS.accentPurple}25, ${COLORS.accentBlue}12)`,
          border: `2px solid ${COLORS.accentPurple}50`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
          boxShadow: isProcessing ? `0 0 40px ${COLORS.accentPurple}25` : `0 0 20px ${COLORS.accentPurple}10`,
          transform: `scale(${interpolate(llmAppear, [0, 1], [0.3, 1])})`,
          opacity: interpolate(llmAppear, [0, 1], [0, 1]),
        }}
      >
        <span style={{ fontSize: 36 }}>🧠</span>
        <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: COLORS.accentPurple, fontWeight: 600 }}>
          gemini-2.5-flash
        </span>
      </div>

      {/* Processing label */}
      {isProcessing && (
        <div
          style={{
            position: "absolute",
            left: LLM_X,
            top: LLM_Y - LLM_R - 28,
            transform: "translate(-50%, 0)",
            fontFamily: FONTS.mono,
            fontSize: 13,
            color: COLORS.accentPurple,
            opacity: frame % 30 < 20 ? 0.9 : 0.4,
          }}
        >
          expanding query...
        </div>
      )}

      {/* === Expanded sub-queries — right === */}
      <div
        style={{
          position: "absolute",
          left: EXPANDED_X,
          top: EXPANDED_Y,
          width: EXPANDED_W,
        }}
      >
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 12,
            color: COLORS.textMuted,
            marginBottom: 12,
            textTransform: "uppercase",
            letterSpacing: 1.5,
            opacity: interpolate(
              spring({ frame: frame - 90, fps, config: SPRING.snappy }),
              [0, 1],
              [0, 1],
            ),
          }}
        >
          Rewritten + Decomposed Sub-Queries (5)
        </div>

        {expandedQueries.map((query, i) => {
          const qAppear = spring({
            frame: frame - 100 - i * 12,
            fps,
            config: SPRING.snappy,
          });

          return (
            <div
              key={i}
              style={{
                padding: "12px 18px",
                marginBottom: 10,
                borderRadius: 10,
                background: `${COLORS.accentPurple}08`,
                border: `1px solid ${COLORS.accentPurple}25`,
                fontFamily: FONTS.mono,
                fontSize: 13,
                color: COLORS.textSecondary,
                opacity: interpolate(qAppear, [0, 1], [0, 1]),
                transform: `translateX(${interpolate(qAppear, [0, 1], [20, 0])}px)`,
                display: "flex",
                alignItems: "center",
                gap: 10,
                lineHeight: 1.4,
              }}
            >
              <span
                style={{
                  color: COLORS.accentPurple,
                  fontWeight: 700,
                  fontSize: 12,
                  minWidth: 22,
                  flexShrink: 0,
                }}
              >
                Q{i + 1}
              </span>
              {query}
            </div>
          );
        })}
      </div>

      {/* === Bottom stats === */}
      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: "50%",
          transform: "translate(-50%, 0)",
          display: "flex",
          gap: 70,
          alignItems: "center",
        }}
      >
        {[
          { label: "Rewrite + Decompose", value: "source-aware", color: COLORS.accentPurple, delay: 180 },
          { label: "Semantic Cache", value: "MISS", color: COLORS.accentTeal, delay: 190 },
          { label: "Sub-queries", value: "5 generated", color: COLORS.accentBlue, delay: 200 },
        ].map((item, i) => {
          const itemAppear = spring({ frame: frame - item.delay, fps, config: SPRING.snappy });
          return (
            <div key={i} style={{ textAlign: "center", opacity: interpolate(itemAppear, [0, 1], [0, 1]) }}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 26, fontWeight: 700, color: item.color }}>
                {item.value}
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted, marginTop: 4 }}>
                {item.label}
              </div>
            </div>
          );
        })}

        {/* Cache badge */}
        {(() => {
          const cacheAppear = spring({ frame: frame - 210, fps, config: SPRING.snappy });
          return (
            <div
              style={{
                padding: "8px 20px",
                borderRadius: 8,
                background: `${COLORS.accentGreen}12`,
                border: `1px solid ${COLORS.accentGreen}35`,
                fontFamily: FONTS.mono,
                fontSize: 14,
                color: COLORS.accentGreen,
                opacity: interpolate(cacheAppear, [0, 1], [0, 1]),
                transform: `scale(${interpolate(cacheAppear, [0, 1], [0.8, 1])})`,
              }}
            >
              💾 semantic cache miss → retrieving
            </div>
          );
        })()}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
